import React, { useState } from 'react';
import { motion } from 'framer-motion';
import * as Lucide from "lucide-react";
import { playSound, SharedSoundEffect, RootState } from '@mental-mint/shared-ui';
import { useSelector } from 'react-redux';

interface HintTutorialOverlayProps {
  /** Whether to show the hint tutorial overlay */
  show: boolean;
  /** The game color for styling */
  gameColor: string;
  /** Callback when user accepts the hint */
  onAccept: (dontShowAgain: boolean) => void;
  /** Callback when user declines the hint */
  onDecline: (dontShowAgain: boolean) => void;
  /** The time penalty for using the hint (in seconds) */
  timePenalty: number;
}

export const HintTutorialOverlay: React.FC<HintTutorialOverlayProps> = ({
  show,
  gameColor,
  onAccept,
  onDecline,
  timePenalty
}) => {
  const { currentGame } = useSelector((state: RootState) => state.gameSession);
  const game = currentGame!;
  const [dontShowAgain, setDontShowAgain] = useState(false);
  
  if (!show) return null;

  return (
    <>
      {/* Dark background overlay */}
      <div style={{
        position: "fixed",
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        backgroundColor: "rgba(0, 0, 0, 0.8)",
        zIndex: 1000,
        pointerEvents: "auto"
      }} />
      
      {/* Modal container - centered */}
      <div style={{
        position: "fixed",
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        zIndex: 1001,
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        pointerEvents: "none"
      }}>
        <motion.div
          initial={{ opacity: 0, scale: 0.9 }}
          animate={{ opacity: 1, scale: 1 }}
          exit={{ opacity: 0, scale: 0.9 }}
          style={{
            backgroundColor: "rgba(255, 255, 255, 0.98)",
            borderRadius: "1rem",
            boxShadow: "0 8px 32px rgba(0, 0, 0, 0.3)",
            maxWidth: "90%",
            width: "400px",
            padding: "2rem",
            boxSizing: "border-box",
            textAlign: "center",
            fontFamily: game.fontSecondary,
            pointerEvents: "auto"
          }}
        >
      <div style={{
        fontSize: "1.5rem",
        fontWeight: "600",
        color: gameColor,
        marginBottom: "1rem"
      }}>
        Need a hint?
      </div>
      
      <div style={{
        marginBottom: "1.5rem",
        fontSize: "1.05rem",
        color: "#4a5568",
        lineHeight: "1.6"
      }}>
        <span style={{ margin: "0 0 0.75rem 0" }}>
          <span style={{display: 'inline-flex', alignItems: 'center', gap: '0.25rem', margin: '0 0.4rem'}}>
            Pay <Lucide.AlarmClockMinus size={18} strokeWidth={3} color={gameColor}/> <span style={{fontWeight: "600", color: gameColor}}>{timePenalty} sec</span>
          </span> 
          to eliminate <br />the least helpful words
        </span>
      </div>
      
      <div style={{
        display: "flex",
        gap: "1rem",
        justifyContent: "center",
        marginBottom: "1rem"
      }}>
        <button
          onClick={() => {
            playSound(SharedSoundEffect.MENU_CLICK);
            onDecline(dontShowAgain);
          }}
          style={{
            backgroundColor: "rgba(0, 0, 0, 0.1)",
            color: "#4a5568",
            border: "1px solid rgba(0, 0, 0, 0.2)",
            padding: "0.75rem 1.5rem",
            borderRadius: "0.5rem",
            fontSize: "1rem",
            fontWeight: "600",
            cursor: "pointer",
            boxShadow: "0 2px 8px rgba(0, 0, 0, 0.05)",
            transition: "all 0.2s ease",
            fontFamily: game.fontSecondary
          }}
        >
          No thanks
        </button>
        
        <button
          onClick={() => {
            playSound(SharedSoundEffect.MENU_CLICK);
            onAccept(dontShowAgain);
          }}
          style={{
            backgroundColor: gameColor,
            color: "white",
            border: "none",
            padding: "0.75rem 1.5rem",
            borderRadius: "0.5rem",
            fontSize: "1rem",
            fontWeight: "600",
            cursor: "pointer",
            boxShadow: "0 2px 8px rgba(0, 0, 0, 0.1)",
            transition: "all 0.2s ease",
            fontFamily: game.fontSecondary,
            display: "flex",
            alignItems: "center",
            gap: "0.5rem"
          }}
        >
          <Lucide.Lightbulb size={17} strokeWidth={2} />
          Get hint
        </button>
      </div>
      
      {/* Checkbox for "don't show again" */}
      <div style={{
        marginTop: "1rem",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        gap: "0.5rem"
      }}>
        <input
          type="checkbox"
          id="dontShowAgain"
          checked={dontShowAgain}
          onChange={(e) => setDontShowAgain(e.target.checked)}
          style={{
            cursor: "pointer",
            width: "16px",
            height: "16px"
          }}
        />
        <label
          htmlFor="dontShowAgain"
          style={{
            cursor: "pointer",
            fontSize: "0.95rem",
            color: "#4a5568",
            userSelect: "none"
          }}
        >
          Don't show again
        </label>
      </div>
        </motion.div>
      </div>
    </>
  );
};
